import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { RingaLogo } from '../brand/RingaLogo';
import { Rings } from '../brand/Rings';
import { Scramble } from '../brand/Scramble';
import { AcceptDialog, AdjustDialog, acceptedLabel } from '../components/ResponseDialogs';
import { api, ApiError } from '../lib/api';
import { fmtDate, validityLabel } from '../lib/format';
import { startReading, type Reading, type SectionInfo } from '../lib/reading';
import type { OpenView, Plan, SelectionItem } from '../lib/types';

const Clock = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <circle cx="8" cy="8" r="6.3" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 4.6V8l2.2 1.4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const Check = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Viewer = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState<OpenView | null>(null);
  const [missing, setMissing] = useState(false);
  const [sections, setSections] = useState<SectionInfo[]>([]);
  const [dialog, setDialog] = useState<'accept' | 'adjust' | 'renewal' | null>(null);
  const [selection, setSelection] = useState<SelectionItem[]>([]);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [section, setSection] = useState<string | null>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const reading = useRef<Reading | null>(null);
  const opened = useRef(false);

  // one open per mount (StrictMode re-runs effects; a second open would count a phantom reading)
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    api<OpenView>('/api/views', { body: { slug } })
      .then((v) => {
        setView(v);
        document.title = `${v.meta.title} · Ringa`;
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) navigate(`/?next=${encodeURIComponent(`/p/${slug}`)}`, { replace: true });
        else setMissing(true);
      });
  }, [slug, navigate]);

  const contentUrl = view?.contentUrl;
  const trackedViewId = view && !view.preview ? (view.viewId ?? null) : null;
  useEffect(() => {
    if (!contentUrl || !frame.current) return;
    const r = startReading({ viewId: trackedViewId, frame: frame.current, onSections: setSections });
    reading.current = r;
    return () => r.stop();
  }, [contentUrl, trackedViewId]);

  const openAccept = useCallback(async () => {
    const snap = (await reading.current?.snapshot()) ?? { items: [], plan: null, section: null };
    setSelection(snap.items);
    setPlan(snap.plan);
    setDialog('accept');
  }, []);
  const openAdjust = useCallback(() => {
    setSection(reading.current?.currentSection() ?? null);
    setDialog('adjust');
  }, []);

  if (missing) {
    return (
      <main className="vstate">
        <Rings />
        <div className="vstate-in">
          <RingaLogo className="vstate-logo" />
          <h1>Não encontramos esta proposta.</h1>
          <p>Ela pode ter sido removida ou não estar liberada para o seu e-mail.</p>
          <Link className="btn btn-ghost" to="/propostas">
            Ver minhas propostas
          </Link>
        </div>
      </main>
    );
  }
  if (!view) {
    return (
      <div className="center" style={{ minHeight: '100dvh' }}>
        <div className="spinner" />
      </div>
    );
  }

  const { meta } = view;
  const accepted = meta.accepted;
  const preview = !!view.preview;

  if (view.expired) {
    return (
      <main className="vstate">
        <Rings />
        <div className="vstate-in">
          <RingaLogo className="vstate-logo" />
          <p className="kick">{meta.title}</p>
          <h1>Esta proposta venceu em {fmtDate(meta.expiresAt)}.</h1>
          <p>Os valores e prazos podem ter mudado. Peça uma renovação e a Ringa envia a versão atualizada.</p>
          <div className="vstate-row">
            <button className="btn" onClick={() => setDialog('renewal')}>
              <Scramble>Pedir renovação</Scramble>
            </button>
            <Link className="btn btn-ghost" to="/propostas">
              Minhas propostas
            </Link>
          </div>
        </div>
        <AdjustDialog open={dialog === 'renewal'} onClose={() => setDialog(null)} slug={slug} viewId={null} sections={[]} section={null} renewal />
      </main>
    );
  }

  return (
    <div className="viewer">
      <header className="vbar">
        <Link className="vbar-brand" to="/propostas" aria-label="Ringa, suas propostas">
          <RingaLogo className="vbar-logo" />
        </Link>
        <span className="vbar-sep" aria-hidden="true" />
        <div className="vbar-title">
          <strong>{meta.title}</strong>
          <span>
            {meta.clientName} · v{meta.versionNumber}
          </span>
        </div>
        <div className="vbar-right">
          {preview && <span className="chip chip-preview">Pré-visualização · métricas desligadas</span>}
          {accepted ? (
            <span className="chip chip-ok">
              <Check />
              {acceptedLabel(accepted)}
            </span>
          ) : (
            <span className="chip chip-time" title={`Válida até ${fmtDate(meta.expiresAt)}`}>
              <Clock />
              {validityLabel(meta.expiresAt, meta.expired)}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={openAdjust} disabled={preview}>
            <Scramble>{accepted ? 'Falar com a Ringa' : 'Pedir ajuste'}</Scramble>
          </button>
          {!accepted && (
            <button className="btn btn-sm vbar-accept" onClick={openAccept} disabled={preview}>
              <Scramble>Aceitar proposta</Scramble>
            </button>
          )}
        </div>
      </header>

      <iframe
        ref={frame}
        className="vframe"
        title={meta.title}
        src={view.contentUrl}
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-downloads"
        referrerPolicy="no-referrer"
      />

      <AcceptDialog
        open={dialog === 'accept'}
        onClose={() => setDialog(null)}
        meta={meta}
        viewId={view.viewId ?? null}
        selection={selection}
        plan={plan}
        onAccepted={(a) => setView({ ...view, meta: { ...meta, accepted: a } })}
      />
      <AdjustDialog open={dialog === 'adjust'} onClose={() => setDialog(null)} slug={slug} viewId={view.viewId ?? null} sections={sections} section={section} />
    </div>
  );
};
